import { createRouter, createWebHistory, RouteRecordRaw, RouterView } from 'vue-router'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useAuthStore } from '../stores/auth'
import { useSignalRStore } from '../stores/signalr'
import { login, validateLogin } from '@/utils/auth'
import RouteViewComponent from '../layouts/RouterBypass.vue'

const cfg = window.Config

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: {
      requiresAuth: true,
    },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('@/pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('@/pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('@/pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('@/pages/users/UsersPage.vue'),
      },
      {
        path: 'devices',
        name: 'devices',
        redirect: { name: 'devices-list' },
        component: RouterView,
        children: [
          {
            name: 'devices-list',
            path: '',
            component: () => import('@/pages/devices/DevicesPage.vue'),
          },
          {
            name: 'device-detail',
            path: 'detail/:id',
            component: () => import('@/pages/devices/DeviceDetailPage.vue'),
          },
        ],
      },
      {
        name: 'projects',
        path: 'projects',
        redirect: { name: 'projects-list' },
        component: RouterView,
        children: [
          {
            name: 'projects-list',
            path: '',
            component: () => import('@/pages/projects/ProjectsPage.vue'),
          },
          {
            name: 'project-detail',
            path: 'detail/:id',
            component: () => import('@/pages/projects/ProjectDetailPage.vue'),
          },
          {
            name: 'new-project',
            path: 'new',
            component: () => import('@/pages/projects/EasyNewProjectPage.vue'),
          },
        ],
      },
      {
        path: 'sparepart',
        name: 'sparepart',
        component: () => import('@/pages/spareparts/SparePartsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('@/pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('@/pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('@/pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('@/pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('@/pages/auth/Login.vue'),
      },
      {
        name: 'logout',
        path: 'logout',
        component: () => import('@/pages/auth/Logout.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('@/pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('@/pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('@/pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('@/pages/404.vue'),
  },
]

async function initAuth() {
  const { startSignalR } = useSignalRStore()
  const authStore = useAuthStore()

  if (cfg.LoginPage) await validateLogin()
  else await login({ password: '' })

  if (authStore.isLoggedIn) startSignalR(authStore.jwt)

  if (cfg.ReCaptcha) {
    await new Promise<void>((resolve) => {
      const check = () => {
        if (typeof cfg.ReCaptcha === 'undefined') {
          setTimeout(check, 100)
        } else {
          resolve()
        }
      }

      check()
    })
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeResolve(async (to, from, next) => {
  // const title = i18n.global.t(titles[to.name as keyof typeof titles])
  // document.title = title + ' - ' + name

  const authStore = useAuthStore()
  // this will only be null on first route
  if (from.name == null) {
    try {
      await initAuth()
    } catch (error) {
      console.error(error)
    }
  }

  if (to.path.startsWith('/auth/') && authStore.isLoggedIn) {
    next({ path: '/dashboard' })
    return
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!authStore.isLoggedIn) {
      next({
        path: '/auth/login',
        query: { redirect: to.fullPath },
      })

      return
    }

    if (to.matched.some((record) => record.meta.requiresAdmin)) {
      if (authStore.user === undefined || authStore.user.role != RoleFlag.Manager) {
        next({ path: '/403' })
        return
      }
    }
  }

  next()
})

export default router
